import dependants from '~/assets/dependants.json';
import { FILTER_COMPATIBILITY, FILTER_PLATFORMS } from '~/components/Filters/helpers';
import { type DependantsDataType, type LibraryType, type Query, type QueryFilters } from '~/types';

import { getNewArchSupportStatus, NewArchSupportStatus } from './newArchStatus';
import { relevance } from './sorting';
import { isEmptyOrNull } from './strings';

const NPM_NAME_CLEANUP_REGEX = /[-/]/g;
const GITHUB_URL_CLEANUP_REGEX =
  /^https?:\/\/(?:www\.)?github\.com\/([^/]+\/[^/]+)(?:$|\/|\.git).*$/;
const DEPENDANTS = dependants as DependantsDataType;

const SUPPORT_PARAMS = [
  ...FILTER_PLATFORMS.map(filter => filter.param),
  ...FILTER_COMPATIBILITY.map(filter => filter.param),
];

function calculateMatchScore(
  { github, npmPkg, topicSearchString, unmaintained, githubUrl, vegaos, score, npm }: LibraryType,
  querySearch: string
) {
  const exactNameMatchPoints =
    (!isEmptyOrNull(github.name) && github.name.toLowerCase() === querySearch) ||
    (!isEmptyOrNull(githubUrl) && githubUrl.toLowerCase() === querySearch) ||
    (!isEmptyOrNull(npmPkg) && npmPkg.toLowerCase() === querySearch)
      ? 150
      : 0;

  const cleanedUpName = npmPkg
    .replace('react-native', '')
    .replace('react', '')
    .replaceAll(NPM_NAME_CLEANUP_REGEX, ' ')
    .toLowerCase()
    .trim();

  const cleanedUpQuery = querySearch
    .replace('react-native', '')
    .replace('react', '')
    .replaceAll(NPM_NAME_CLEANUP_REGEX, ' ')
    .trim();

  const queryTokens = cleanedUpQuery.split(/\s+/).filter(Boolean);
  const allTokensMatch =
    queryTokens.length > 0 && queryTokens.every(token => cleanedUpName.includes(token));

  const npmPkgNameMatchPoints =
    !isEmptyOrNull(npmPkg) &&
    (npmPkg.includes(querySearch) ||
      npmPkg.replaceAll(NPM_NAME_CLEANUP_REGEX, ' ').toLowerCase().includes(querySearch))
      ? 125
      : !isEmptyOrNull(npmPkg) && allTokensMatch
        ? 100
        : 0;

  const cleanedUpNameMatchPoints =
    !isEmptyOrNull(cleanedUpName) &&
    (cleanedUpName.includes(querySearch) ||
      cleanedUpName.includes(querySearch.replaceAll(NPM_NAME_CLEANUP_REGEX, ' ')))
      ? 100
      : !isEmptyOrNull(cleanedUpName) && allTokensMatch
        ? 75
        : 0;

  const gitHubURLOrOwnerMatchPoints =
    githubUrl.startsWith(querySearch) ||
    githubUrl.replace(GITHUB_URL_CLEANUP_REGEX, '$1').toLowerCase().includes(querySearch)
      ? 50
      : 0;

  const repoNameMatchPoints =
    !isEmptyOrNull(github.name) && github.name.toLowerCase().includes(querySearch)
      ? 50
      : !isEmptyOrNull(github.name) && allTokensMatch
        ? 35
        : 0;

  const vegaOSPackageMatchPoints =
    typeof vegaos === 'string' && vegaos.includes(querySearch) ? 25 : 0;

  const descriptionMatchPoints =
    !isEmptyOrNull(github.description) && github.description?.toLowerCase().includes(querySearch)
      ? 25
      : 0;

  const topicMatchPoints = topicSearchString.includes(querySearch) ? 10 : 0;

  const matchScore =
    exactNameMatchPoints +
    repoNameMatchPoints +
    vegaOSPackageMatchPoints +
    cleanedUpNameMatchPoints +
    npmPkgNameMatchPoints +
    gitHubURLOrOwnerMatchPoints +
    descriptionMatchPoints +
    topicMatchPoints;

  if (matchScore && unmaintained) {
    return matchScore / 1000;
  } else if (npm?.downloads && npm.downloads < 10_000) {
    return matchScore / 10;
  } else {
    return matchScore > 0 ? matchScore + score / 2 : matchScore;
  }
}

export function handleFilterLibraries({
  libraries,
  sortBy,
  queryTopic,
  querySearch,
  support,
  hasExample,
  hasImage,
  hasTypes,
  hasNativeCode,
  configPlugin,
  isMaintained,
  isPopular,
  wasRecentlyUpdated,
  minPopularity,
  minMonthlyDownloads,
  newArchitecture,
  dev,
  skipLibs,
  skipTools,
  expoModule,
  nitroModule,
  turboModule,
  nightlyProgram,
  owner,
  dependantsOf,
  bookmarks,
  bookmarkedIds,
}: Query & QueryFilters & { bookmarkedIds?: Set<string> | null }) {
  const viewerHasChosenTopic = !isEmptyOrNull(queryTopic);
  const viewerHasTypedSearch = !isEmptyOrNull(querySearch);

  const minPopularityValue = minPopularity && Number.parseFloat(minPopularity) / 100;
  const minMonthlyDownloadsValue = minMonthlyDownloads && Number.parseInt(minMonthlyDownloads, 10);
  const dependantNames = dependantsOf
    ? new Set(DEPENDANTS[dependantsOf.toLowerCase()]?.map(name => name.toLowerCase()))
    : undefined;

  const processedLibraries = viewerHasTypedSearch
    ? libraries.map(library => ({
        ...library,
        matchScore: calculateMatchScore(library, querySearch ?? ''),
      }))
    : libraries;

  const filteredLibraries = processedLibraries.filter(library => {
    let isTopicMatch = false;
    let isSearchMatch = false;

    if (bookmarks && bookmarkedIds && !bookmarkedIds.has(library.npmPkg)) {
      return false;
    }

    if (skipLibs && !library.dev) {
      return false;
    }

    if (skipTools && library.dev) {
      return false;
    }

    if (dev === 'true' && !library.dev) {
      return false;
    }

    for (const param of SUPPORT_PARAMS) {
      if (support[param] === 'true' && !library[param as keyof LibraryType]) {
        return false;
      }
      if (support[param] === 'false' && library[param as keyof LibraryType]) {
        return false;
      }
    }

    if (hasExample && !library.examples?.length) {
      return false;
    }

    if (hasImage && !library.images?.length) {
      return false;
    }

    if (hasTypes && !library.github.hasTypes) {
      return false;
    }

    if (hasNativeCode && !library.github.hasNativeCode) {
      return false;
    }

    if (configPlugin && !(library.configPlugin ?? library.github.configPlugin)) {
      return false;
    }

    const newArchStatus = getNewArchSupportStatus(library);

    if (
      newArchitecture === 'true' &&
      [NewArchSupportStatus.Unsupported, NewArchSupportStatus.Untested].includes(newArchStatus)
    ) {
      return false;
    }

    if (
      newArchitecture === 'false' &&
      [
        NewArchSupportStatus.NewArchOnly,
        NewArchSupportStatus.Supported,
        NewArchSupportStatus.Untested,
      ].includes(newArchStatus)
    ) {
      return false;
    }

    if (
      newArchitecture === 'untested' &&
      [
        NewArchSupportStatus.NewArchOnly,
        NewArchSupportStatus.Supported,
        NewArchSupportStatus.Unsupported,
      ].includes(newArchStatus)
    ) {
      return false;
    }

    if (nightlyProgram === 'true' && !library.nightlyProgram) {
      return false;
    }

    if (isMaintained === 'false' && !library.unmaintained) {
      return false;
    }

    if (isMaintained === 'true' && library.unmaintained) {
      return false;
    }

    if (isPopular && !library.matchingScoreModifiers.includes('Popular')) {
      return false;
    }

    if (wasRecentlyUpdated && !library.matchingScoreModifiers.includes('Recently updated')) {
      return false;
    }

    if (owner && !library.githubUrl.startsWith(`https://github.com/${owner}`)) {
      return false;
    }

    if (dependantNames && !dependantNames.has(library.npmPkg.toLowerCase())) {
      return false;
    }

    const activeModuleTypeFilters = [
      expoModule && 'expo',
      nitroModule && 'nitro',
      turboModule && 'turbo',
    ].filter(Boolean);

    if (activeModuleTypeFilters.length > 0) {
      const type = library.github?.moduleType;

      if (!activeModuleTypeFilters.includes(type)) {
        return false;
      }
    }

    if (minPopularityValue && minMonthlyDownloadsValue) {
      return (
        (library?.popularity ?? 0) >= minPopularityValue &&
        (library.npm?.downloads ?? 0) >= minMonthlyDownloadsValue
      );
    } else if (minPopularityValue) {
      return (library?.popularity ?? 0) >= minPopularityValue;
    } else if (minMonthlyDownloadsValue) {
      return (library.npm?.downloads ?? 0) >= minMonthlyDownloadsValue;
    }

    if (!viewerHasChosenTopic && !viewerHasTypedSearch) {
      return true;
    }

    if (viewerHasChosenTopic && queryTopic && library.topicSearchString.includes(queryTopic)) {
      isTopicMatch = true;
    }

    if (!viewerHasTypedSearch) {
      return isTopicMatch;
    }

    isSearchMatch = Boolean(library.matchScore && library.matchScore > 0);

    if (!viewerHasChosenTopic) {
      return isSearchMatch;
    }

    return isTopicMatch && isSearchMatch;
  });

  if (sortBy === 'relevance') {
    return relevance(filteredLibraries);
  }

  return filteredLibraries;
}

export function getPageQuery(basePath: string, query: Query) {
  if (basePath === '/trending') {
    return { ...query, minPopularity: undefined, order: undefined };
  }
  return query;
}
